import {MouseEvent, useState, useCallback, useMemo} from 'react';
import {Assign} from 'utility-types';
import {useSelector, useDispatch} from 'react-redux';
import {useHistory, useLocation} from 'react-router-dom';
import {detailsPagePushGaThunk} from '@stencil/hotels-ga/src';
import {PrimaryLink} from '../../../../../../stories/components/CommonStyleUtils/dwebCommon.styles';
import {defaultTheme} from '@stencil/build-theme';

// COMPONENTs
import RoomInfoText from '../../common/RoomInfoText';
import Modal, {useModal} from '@stencil/hotels-modal-component';
import PayModePersuasionInfo from '../PayAtHotelPersuasion/PayAtHotelPersuasion';
import RatePlanInclusionUI from '../../../../../../stories/components/hotels/DetailsPageUnif/RatePlanInclusionUI/RatePlanInclusionUI';
import AddRoomFlow from '../../../../../../shared/pages/DetailsPageNew/containers/RoomSelection/MultiRoom/AddRoomFlow/AddRoomFlow';
import GoTribeTierDetailsUI from '../../../common/GoTribeTierDetailsUI/GoTribeTierDetailsUI';
import DownsellUI from '../DownsellUI/DownsellUI';

// STYLES
import * as S from './RoomFlavor.styles';

// ICONS
import RupeeIcon from '../../../../../../stories/Icons/RupeeIcon';

// TYPES
import {RoomFlavorProps} from '../Room/types';

import {DayUseParams} from '../../../../../../shared/pages/DetailsPageNew/redux/types';

import {
  getCancellationPolicyColor,
  multiRoomTextCreator,
  RoomCase,
} from '@stencil/hotels-utils/src/details-utils';

// CONFIG
import {appConfig, gaConfig} from '@stencil/hotels-config';

import {
  roomStringSelectorFn,
  isDatePickRequiredSelector,
} from '@stencil/hotels-common-reducer/src/selectors';
import {
  multiRoomFlowStatusSelector,
  isEntirePropertySelector,
  checkInTimeV2Selector,
  isAltAccoUrlPropsSelector,
  entirePropertyMergedTypeSelector,
} from '@stencil/hotels-cg-reducer/src/details/selectors';
import PoliciesModalContent from '../../../../../../stories/components/hotels/common/PoliciesModalContent/PoliciesModalContent';
import CheckAvailabilityBtnUI from '../../CheckAvailabilityAltAcco/CheckAvailabilityBtnUI';
import {userSelector, IUserStoreState} from '@stencil/user-reducer';
import BookNowPayLater from '../../../../../../stories/components/hotels/DetailsPageUnif/Rooms/BookNowPayLater/BookNowPayLater';
import BookNowPayLaterHoverPersuasion from '../../../../../../stories/components/hotels/DetailsPageUnif/Rooms/BookNowPayLaterHoverPersuasion/BookNowPayLaterHoverPersuasion';
import {
  decimalToIntegerPercentage,
  gaDiscountPercentageBuckets,
  gaPriceBuckets,
} from '@stencil/hotels-utils/src/common-utils';
import {setActiveRoomRatePlan} from '@stencil/hotels-common-reducer/src/actions';
import CkiTimePopup from '@stencil/cki-time-popup';
import PlanDetailsPoliciesModal from '../Modal/PlanDetailsandPoliciesModal';
import HappyTickMarkIcon from '@stencil/icons/src/HappyTickMarkIcon/HappyTickMarkIcon';
import HappyCloseIcon from '@stencil/icons/src/HappyCloseIcon/HappyCloseIcon';
import RoomPerNightUI from '../../RoomPerNightUI/RoomPerNightUI';
// Omniture Imports
import {omnitureThunk, omnitureConfig} from '@stencil/omniture-analytics/src';
import {
  detailsClick,
  DetailsPageComponents,
} from '@stencil/omniture-analytics/src/constants/details';

//Types
import {Paymodes} from '@stencil/hotels-types/src/type-guards';
import {LinkedRatePlans} from '@stencil/hotels-types/src/details-cg/types';

// from Abinash - Pls separate out the styles into a separate file, under import * as pattern
// currently too much cramped into single file, making readability difficult

// from Abinash - why not directly use this ? because only other substitution we will be going for will be
// icon / svg component

const {rupeeString} = appConfig;
const {DetailsGAClickEventActions, detailsPageClickEventCategoryName} = gaConfig;

const RecommendedLabel = () => (
  <S.RecommendedLabelWrapper>
    <S.RecommendedLabelPrependStyles />
    <S.RecommendedTextStyled>Recommended</S.RecommendedTextStyled>
  </S.RecommendedLabelWrapper>
);

const ElitePackageLabel = ({packageTagImgUrl}) => (
  <S.PackageTag>
    <img src={packageTagImgUrl} alt="package-icon-img" />
  </S.PackageTag>
);

const RoomOptionsHeading: React.FC<{
  children: React.ReactNode;
  isRecommended: boolean;
  isPackageOption: boolean;
  packageTagImgUrl: string;
}> = ({children, isRecommended = false, isPackageOption = false, packageTagImgUrl}) => (
  <>
    {isPackageOption && packageTagImgUrl && (
      <ElitePackageLabel packageTagImgUrl={packageTagImgUrl} />
    )}
    {isRecommended && <RecommendedLabel />}

    <S.RoomHeadingContainer>
      <S.RoomOptionsHeadingTextStyle id="room-options-name">
        {children}
      </S.RoomOptionsHeadingTextStyle>
    </S.RoomHeadingContainer>
  </>
);

const StrikeThroughPersuasionPrice: React.FC<{children: React.ReactNode}> = ({children}) => (
  <S.StrikeThroughPersuasionPriceTextStyled>
    {rupeeString} {children}
  </S.StrikeThroughPersuasionPriceTextStyled>
);

const ActualPrice: React.FC<{children: React.ReactNode; isMultiroom: boolean}> = ({
  children,
  isMultiroom,
}) => (
  <S.ActualPriceTextStyled isMultiroom={isMultiroom}>
    <RupeeIcon {...S.actualPriceRupeeIconProps} />
    {children}
  </S.ActualPriceTextStyled>
);

const TaxesPrice: React.FC<{tax: number}> = ({tax}) => (
  <S.TaxesTextStyled>
    + {rupeeString} {tax} taxes & fees
  </S.TaxesTextStyled>
);

const SelectRoomButton: React.FC<{
  onClickHandler: (event?: MouseEvent | TouchEvent) => void;
  noOfRooms: number;
  isRnpl: boolean;
  selectRoomEventLabel: string;
  roomCount: number;
  roomPlanCode?: string;
  sellableType: string;
  isEntireProperty: boolean;
  propertyTypeTag: string;
}> = ({
  onClickHandler,
  noOfRooms,
  isRnpl = false,
  selectRoomEventLabel = '',
  roomCount,
  roomPlanCode = '',
  sellableType = '',
  isEntireProperty = false,
  propertyTypeTag = '',
}) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const {pathname, search} = useLocation();
  return (
    <S.ButtonWrapper
      onClick={() => {
        dispatch(
          detailsPagePushGaThunk({
            eventAction: DetailsGAClickEventActions.roomSelection.selectButtonClick,
            eventLabel: selectRoomEventLabel,
            customDimensions: {
              cdRateplancode: roomPlanCode,
            },
          }),
        );

        dispatch(
          detailsPagePushGaThunk({
            eventAction: DetailsGAClickEventActions.roomSelection.selectRoomPayMode,
            eventLabel: (isRnpl && 1) || 0,
          }),
        );
        dispatch(setActiveRoomRatePlan(roomCount));
        if (isRnpl)
          dispatch(
            detailsPagePushGaThunk({
              event: detailsPageClickEventCategoryName,
              eventCategory: detailsPageClickEventCategoryName,
              customDimensions: {
                cdRnplCc: true,
              },
            }),
          );

        history.push(pathname + search);

        onClickHandler();
      }}
      data-testid="selectRoomBtn"
    >
      Select{' '}
      {multiRoomTextCreator({
        type: RoomCase.SingleRoom,
        sellableType,
        isEntireProperty,
        propertyTypeTag,
        totalRoomCount: noOfRooms,
      })}
    </S.ButtonWrapper>
  );
};

const cancellationPolicyDefault: HotelCancellationPolicyDataType = {
  cancelPolicy: [],
};

const RoomFlavor: React.FC<{
  roomFlavor: Assign<RoomFlavorProps, {index: number; lastIndex: number}>;
  noInclusionsAndSplInstructionsForAnyRoom: boolean;
  selectRoomClickHandler: () => void;
  roomDefaultImage: string;
  dayUsePrams: DayUseParams;
  roomCode: string;
  handlOpenCalendar?: () => void;
}> = ({
  roomFlavor,
  noInclusionsAndSplInstructionsForAnyRoom,
  selectRoomClickHandler,
  roomDefaultImage,
  dayUsePrams,
  handlOpenCalendar = () => {},
  roomCode = '',
}) => {
  const [modalOpen, setModal] = useState(false);
  const closeGoTribeModal = e => {
    e.stopPropagation();
    setModal(false);
  };

  const openGoTribeModal = e => {
    e.stopPropagation();
    setModal(true);
  };

  // ROOM OPTIONS COLUMN PROPS;
  const roomString = useSelector(roomStringSelectorFn);
  const checkinTime = useSelector(checkInTimeV2Selector);
  const multiRoomFlowStatus = useSelector(multiRoomFlowStatusSelector);
  const isEntireProperty = useSelector(isEntirePropertySelector);
  const isAltAccoUrlProps = useSelector(isAltAccoUrlPropsSelector);
  const isDatePickRequired = useSelector(isDatePickRequiredSelector);
  const mergedPropType = useSelector(entirePropertyMergedTypeSelector);

  const sellableType = roomFlavor?.sellableType;
  const roomStringElements = roomString.split('-');
  const noOfRooms = roomStringElements[0];
  const isMultiRoom = multiRoomFlowStatus?.isMultiRoomFlow;
  const hasOccupancyRooms = multiRoomFlowStatus?.hasOccupancyRooms;

  const {
    roomCount,
    roomFlavorName,
    refundStatusText,
    refundStatus,
    amenities = [],
    index,
    lastIndex,
    isRecommended,
    rnplPerTxt,
    // PRICE COLUMN DATA
    slashedPrice,
    showSlashedPrice,
    showPrice,
    emiData,
    isRnpl,
    tax,
    cancellationPolicyFromHermes = [],
    shouldShowHermesCancellationPolicy,
    cancellationPolicy,
    cancellationTimeline,
    ratePlanRoomCount,
    payModeNew,
    tariffPersuasion,
    goTribeInclusion,
    isPackageOption,
    packageTagImgUrl,
    linkedRatePlans,
    activeBankCode,
    propertyTypeTag: propertyTag,
  } = roomFlavor;
  let selectRoomEventLabel = roomCount + '_' + gaPriceBuckets(showPrice) + '_';

  // switched off member price due to goTribe
  const isMemberPrice = false;

  const payAtHotelPaymode = payModeNew === Paymodes.PAH_WITHOUT_CC;
  const payAtHotelCCRequired = payModeNew === Paymodes.PAH_WITH_CC;

  const payAtHotel = payAtHotelPaymode || payAtHotelCCRequired;

  const [cancellationPolicyData, setCancellationPolicyData] =
    useState<HotelCancellationPolicyDataType>(cancellationPolicyDefault);
  const [cancellationPolicyError, setCancellationPolicyError] = useState<Error | null>(null);

  const {
    isModalOpen: isCancellationPolicyModalOpen,
    openModal: openCancellationModal,
    closeModal: closeCancellationModal,
  } = useModal();

  const {
    isModalOpen: isDayUseModalOpen,
    openModal: openDayUseModal,
    closeModal: closeDayUseModal,
  } = useModal();

  const {
    isModalOpen: isPlanPoliciesModalOpen,
    openModal: openPlanPoliciesModal,
    closeModal: closePlanPoliciesModal,
  } = useModal();

  const userDetails = useSelector(userSelector) as IUserStoreState;

  const dispatch = useDispatch();

  const isLoggedIn = !!userDetails?.isLoggedIn;

  const cancellationPolicyFromHermesShouldBeShown =
    shouldShowHermesCancellationPolicy && !!cancellationPolicyFromHermes.length;

  const cancellationPolicyList =
    cancellationPolicyData && cancellationPolicyData?.cancelPolicy.length
      ? cancellationPolicyData.cancelPolicy
      : cancellationPolicyFromHermesShouldBeShown
        ? cancellationPolicyFromHermes
        : [];

  const showLoader = cancellationPolicyFromHermesShouldBeShown
    ? false
    : !cancellationPolicyData.cancelPolicy.length && !cancellationPolicyError;

  const firstLinkedRatePlan = useMemo(() => {
    if (linkedRatePlans) {
      return linkedRatePlans?.[0];
    } else return {} as LinkedRatePlans;
  }, [linkedRatePlans?.[0]]);

  const toggleSiginPopupCaller = useCallback(() => {
    if (window && document) {
      document.querySelector('#get_sign_in').click();
    }
  }, []);

  // in case of entire properties like apartments/villas
  const propertyTypeTag = useMemo(() => {
    let tag = propertyTag;
    const propertyTypeTagArr = tag?.split(' ') || [];
    if (propertyTypeTagArr.length > 1) {
      tag = propertyTypeTagArr[1];
    }
    return tag;
  }, [propertyTag]);

  const getPriceBlock = () => {
    return (
      <>
        {!payAtHotel && !!slashedPrice && showSlashedPrice && slashedPrice != showPrice && (
          <StrikeThroughPersuasionPrice>{slashedPrice}</StrikeThroughPersuasionPrice>
        )}
        <ActualPrice isMultiroom={hasOccupancyRooms}>{showPrice ? showPrice : 0}</ActualPrice>
        {!!tax && <TaxesPrice tax={tax} />}
        {!isMultiRoom && ratePlanRoomCount && (
          <S.PerRoomNightTextStyled>
            <RoomPerNightUI
              roomText={multiRoomTextCreator({
                type: RoomCase.SingleRoom,
                sellableType,
                isEntireProperty: isEntireProperty || !!mergedPropType,
                propertyTypeTag,
                totalRoomCount: noOfRooms as unknown as number,
              })}
            />
          </S.PerRoomNightTextStyled>
        )}
      </>
    );
  };

  return (
    <S.RoomFlavorWrap
      lastIndex={index === lastIndex}
      data-testid="detail-roomSelection-room-flavor"
    >
      {/* ROOM OPTIONS */}
      <S.RoomFlavorColumn
        isMultiRoom={hasOccupancyRooms}
        isNoInclusions={noInclusionsAndSplInstructionsForAnyRoom}
      >
        <RoomOptionsHeading
          isRecommended={isRecommended}
          isPackageOption={isPackageOption}
          packageTagImgUrl={packageTagImgUrl}
        >
          {roomFlavorName ? `${index + 1 + '. ' + roomFlavorName}` : `OPTION ${index}`}
        </RoomOptionsHeading>
        <S.RoomOptionsBody>
          {
            <>
              <PayModePersuasionInfo paymode={payModeNew} />
              {!!isRnpl && rnplPerTxt && cancellationTimeline ? (
                <BookNowPayLaterHoverPersuasion
                  direction="right"
                  cancellationTimeline={cancellationTimeline}
                  enableHover={cancellationTimeline?.cancellationDate}
                  checkinTime={checkinTime}
                  bnplText={rnplPerTxt}
                >
                  <BookNowPayLater text={rnplPerTxt} />
                </BookNowPayLaterHoverPersuasion>
              ) : null}
              <div
                style={{
                  paddingTop: '11px',
                }}
              >
                {tariffPersuasion?.roomInclusionPersuasion?.length && (
                  <div
                    style={{
                      marginRight: '40px',
                    }}
                  >
                    <RatePlanInclusionUI
                      ratePlanInclusionData={tariffPersuasion?.roomInclusionPersuasion}
                      isMultiRoom={hasOccupancyRooms}
                    />
                  </div>
                )}
                {amenities?.length > 0 ? (
                  amenities.map((amenity, i) => {
                    return i < 5 && amenity?.text ? (
                      <div
                        key={`${amenity?.text}-${i}`}
                        style={{
                          marginBottom: '5px',
                        }}
                      >
                        <RoomInfoText display={'block'}>
                          {amenity?.packageBenefit && (amenity?.iUrl || amenity?.iconUrl) ? (
                            <img
                              src={amenity?.iUrl || amenity?.iconUrl}
                              width={'16px'}
                              height={'16px'}
                            />
                          ) : (
                            <S.BulletPointOuter>
                              <S.BulletPoint />
                            </S.BulletPointOuter>
                          )}

                          <span
                            style={{
                              width: '300px',
                              marginLeft: '5px',
                            }}
                          >
                            <span
                              dangerouslySetInnerHTML={{
                                __html: amenity?.text || '',
                              }}
                            />
                          </span>
                        </RoomInfoText>
                      </div>
                    ) : null;
                  })
                ) : (
                  <div
                    style={{
                      visibility: 'hidden',
                    }}
                  >
                    <RoomInfoText display={'block'}>
                      <S.BulletPointOuter>
                        <S.BulletPoint />
                      </S.BulletPointOuter>
                      <span
                        style={{
                          width: '300px',
                        }}
                      >
                        {}
                      </span>
                    </RoomInfoText>
                  </div>
                )}

                {cancellationPolicy?.text && (
                  <div>
                    <RoomInfoText
                      textColor={getCancellationPolicyColor(cancellationPolicy?.type)}
                      alignItems="center"
                    >
                      <S.RoomOptionsIconContainer>
                        {cancellationPolicy?.type == 'NR' ? (
                          <HappyCloseIcon width="1rem" height="1rem" fill={'#d0011b'} />
                        ) : (
                          <HappyTickMarkIcon
                            fill={defaultTheme.colors.goGreen.base}
                            width="1rem"
                            height="1rem"
                          />
                        )}
                      </S.RoomOptionsIconContainer>

                      <S.CancellationPolicyContainer>
                        <span dangerouslySetInnerHTML={{__html: cancellationPolicy?.text}} />
                      </S.CancellationPolicyContainer>
                    </RoomInfoText>
                  </div>
                )}
              </div>
            </>
          }
          {/* goTribe */}
          {goTribeInclusion?.benefitsList?.length > 0 && (
            <S.GoTribeTextWrapper>
              <S.GoTribeTextInnerWrapper>
                <S.headerWrapper>
                  <S.BulletPointOuter>
                    <S.BulletPoint />
                  </S.BulletPointOuter>
                  <S.goTribeIcon src={goTribeInclusion?.goTribeIconUrlV2} />
                </S.headerWrapper>
                <S.contentContainer>
                  <S.ContentDescription
                    title={
                      goTribeInclusion?.benefitsList?.length == 1
                        ? goTribeInclusion?.benefitsList?.[0]?.text
                        : null
                    }
                  >
                    {goTribeInclusion?.benefitsList
                      ?.slice(0, 3)
                      ?.map(benefitsItem => benefitsItem.text)
                      ?.join(', ')}
                  </S.ContentDescription>{' '}
                  {goTribeInclusion?.benefitsList?.length > 3 && (
                    <S.MoreModalWrapper>
                      <S.MoreButtonWrapper
                        onMouseEnter={openGoTribeModal}
                        onMouseLeave={closeGoTribeModal}
                      >
                        +more
                      </S.MoreButtonWrapper>
                      {/* Gotribe Modal */}
                      <GoTribeTierDetailsUI
                        goTribeRevampPersuasion={
                          {
                            info: {
                              goTribeIconUrlV2: goTribeInclusion?.goTribeIconUrlV2,
                              ctaText: goTribeInclusion?.ctaText,
                            },
                            data: goTribeInclusion?.benefitsList?.map(benefitsItem => {
                              return {
                                pt: benefitsItem?.text,
                                iu: benefitsItem?.iUrl,
                              };
                            }),
                          } as any
                        }
                        isVisible={modalOpen}
                      />
                    </S.MoreModalWrapper>
                  )}
                </S.contentContainer>
              </S.GoTribeTextInnerWrapper>
            </S.GoTribeTextWrapper>
          )}
          <S.CancellationPolicyContainer>
            <S.ViewPlanDetailsPoliciesBtn
              isNoRefundText={!refundStatusText}
              onClick={e => {
                dispatch(
                  omnitureThunk(omnitureConfig.pages.DetailsUnif, omnitureConfig.events.ctaClick, {
                    cta: {
                      name: `${DetailsPageComponents.Room}_${detailsClick.roomSelection.viewPlanDetailsClick}`,
                      type: omnitureConfig.ctaType.Button,
                      componentName: DetailsPageComponents.Room,
                      destination: '',
                    },
                  }),
                );
                openPlanPoliciesModal();
              }}
            >
              View plan details & policies
            </S.ViewPlanDetailsPoliciesBtn>
          </S.CancellationPolicyContainer>
          <Modal
            isOpen={isPlanPoliciesModalOpen}
            closeModal={closePlanPoliciesModal}
            modalStyles={{width: '80rem'}}
            hideCloseButton={true}
          >
            <PlanDetailsPoliciesModal
              roomData={roomFlavor}
              onCloseBtnClick={closePlanPoliciesModal}
            />
          </Modal>

          <Modal
            isOpen={isCancellationPolicyModalOpen}
            closeModal={closeCancellationModal}
            modalStyles={{width: 'auto'}}
          >
            <PoliciesModalContent
              cancellationPolicy={cancellationPolicyList}
              showLoader={showLoader}
              policyError={!!cancellationPolicyError}
            />
          </Modal>
        </S.RoomOptionsBody>
      </S.RoomFlavorColumn>

      <S.RoomFlavorColumn
        isMultiroom={hasOccupancyRooms}
        isNoInclusions={noInclusionsAndSplInstructionsForAnyRoom}
      >
        {
          <>
            {!isMultiRoom &&
              !!tariffPersuasion?.discountRpc?.length &&
              tariffPersuasion.discountRpc.map(discountItem => {
                const {persuasionText: persuationText, colourCodeData: ccd} = discountItem;
                const percentageString = decimalToIntegerPercentage(persuationText);

                const {textColour: textColor, bgColour: bgcolor} = ccd;
                selectRoomEventLabel += gaDiscountPercentageBuckets(persuationText.split('%')[0]);
                return (
                  <S.OuterBlockWrapper>
                    <S.PercentageOffWrapperDiv textColor={textColor} bgColor={bgcolor}>
                      {percentageString}
                    </S.PercentageOffWrapperDiv>
                    <S.PercentageLabelStyles />
                  </S.OuterBlockWrapper>
                );
              })}
            <div>
              {!isMultiRoom && isLoggedIn && isMemberPrice && (
                <>
                  <S.MemberPriceTag>Member Price!</S.MemberPriceTag>
                  <S.MemberPriceWrapDiv>{getPriceBlock()}</S.MemberPriceWrapDiv>
                </>
              )}
              {(!isLoggedIn || !isMemberPrice) && getPriceBlock()}

              {!isMultiRoom && emiData.emiStartingPrice && (
                <S.EMICTAText>
                  <PrimaryLink
                    onClick={() => {
                      dispatch(
                        omnitureThunk(
                          omnitureConfig.pages.DetailsUnif,
                          omnitureConfig.events.ctaClick,
                          {
                            cta: {
                              name: `${DetailsPageComponents.Room}_${detailsClick.roomSelection.eMIModalOpenerClick}`,
                              type: omnitureConfig.ctaType.Button,
                              componentName: DetailsPageComponents.Room,
                              destination: '',
                            },
                          },
                        ),
                      );
                    }}
                  >
                    EMI Starts at {rupeeString} {Math.round(emiData.emiStartingPrice)}/month
                  </PrimaryLink>
                </S.EMICTAText>
              )}
            </div>
            {linkedRatePlans && (
              <DownsellUI
                linkedRatePlan={firstLinkedRatePlan}
                roomCode={roomCode}
                activeBankCode={activeBankCode}
              />
            )}
            <div>
              {isAltAccoUrlProps && isDatePickRequired ? (
                <S.CheckAvailabilityBtnUIWrapper>
                  <CheckAvailabilityBtnUI handlOpenCalendar={handlOpenCalendar} />
                </S.CheckAvailabilityBtnUIWrapper>
              ) : hasOccupancyRooms ? (
                <AddRoomFlow roomData={roomFlavor} roomDefaultImage={roomDefaultImage} />
              ) : (
                <>
                  <SelectRoomButton
                    roomCount={roomCount}
                    noOfRooms={noOfRooms}
                    onClickHandler={dayUsePrams?.idur ? openDayUseModal : selectRoomClickHandler}
                    isRnpl={!!isRnpl}
                    selectRoomEventLabel={
                      selectRoomEventLabel +
                      '_' +
                      (refundStatus ? 'FCY' : 'FCN') +
                      '_' +
                      (!!isRnpl ? 'BNPLY' : 'BNPN')
                    }
                    roomPlanCode={roomFlavor?.roomFlavorName}
                    sellableType={sellableType}
                    isEntireProperty={isEntireProperty || !!mergedPropType}
                    propertyTypeTag={propertyTypeTag}
                  />
                  {dayUsePrams?.idur ? (
                    <Modal
                      isOpen={isDayUseModalOpen}
                      closeModal={closeDayUseModal}
                      CloseButton={() => null}
                      modalStyles={{width: 'auto'}}
                    >
                      <CkiTimePopup
                        onProceed={selectRoomClickHandler}
                        isPWA={false}
                        onCancel={closeDayUseModal}
                        dayUsePrams={dayUsePrams?.dup}
                      />
                    </Modal>
                  ) : null}
                </>
              )}

              {!isMultiRoom && !isLoggedIn && (
                <PrimaryLink
                  onClick={() => {
                    dispatch(
                      omnitureThunk(
                        omnitureConfig.pages.DetailsUnif,
                        omnitureConfig.events.ctaClick,
                        {
                          cta: {
                            name: `${DetailsPageComponents.Room}_${detailsClick.roomSelection.signUpInPersuasionTextClick}`,
                            type: omnitureConfig.ctaType.Button,
                            componentName: DetailsPageComponents.Room,
                            destination: '',
                          },
                        },
                      ),
                    );

                    toggleSiginPopupCaller();
                  }}
                >
                  <S.LoginForMore>Login Now to unlock best deals and offers!</S.LoginForMore>
                </PrimaryLink>
              )}
            </div>
          </>
        }
      </S.RoomFlavorColumn>
    </S.RoomFlavorWrap>
  );
};

export default RoomFlavor;

/**
 * the commented properties are not used
 */
type HotelCancellationPolicyDataType = {
  cancelPolicy: string[];
};

// REFACTOR – when types are extracted in separate package
async function fetchCancellationPolicyData(event: MouseEvent<HTMLSpanElement>) {
  event.preventDefault();
}
